import * as React from 'react';
import { useState, useEffect } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { TextField, Box, Paper, Typography, Popover } from '@mui/material';
import { parseLocalDate } from 'src/utils';

interface DatePickerProps {
  variant?: 'form' | 'inline';
  startDate?: string | null;
  endDate?: string | null;
  showPlaceholder?: boolean;
  onChange?: (dateRange: { start: string | null; end: string | null }) => void;
  width?: number;
  disabled?: boolean;
  placeholder?: string;
  minDate?: Date | null;
}

const DatePicker: React.FC<DatePickerProps> = ({
  variant = 'form',
  startDate = null,
  endDate = null,
  showPlaceholder = true,
  onChange,
  width = 210,
  disabled = false,
  placeholder = 'Select date range',
  minDate = null,
}) => {
  const [internalStartDate, setInternalStartDate] = useState<Date | null>(startDate ? parseLocalDate(startDate) : null);
  const [internalEndDate, setInternalEndDate] = useState<Date | null>(endDate ? parseLocalDate(endDate) : null);
  const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectingStartDate, setSelectingStartDate] = useState<boolean>(true);

  useEffect(() => {
    setInternalStartDate(startDate ? parseLocalDate(startDate) : null);
  }, [startDate]);

  useEffect(() => {
    setInternalEndDate(endDate ? parseLocalDate(endDate) : null);
  }, [endDate]);

  const formatDateRange = () => {
    if (internalStartDate && internalEndDate) {
      return `${internalStartDate.toLocaleDateString()} - ${internalEndDate.toLocaleDateString()}`;
    } else {
      return placeholder;
    }
  };

  const handleCalendarOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
      setIsCalendarOpen(true);
      setSelectingStartDate(true);
    }
  };

  const handleCalendarClose = () => {
    setIsCalendarOpen(false);
    setAnchorEl(null);
  };

  const handleDateSelect = (newDate: Date | null) => {
    if (!newDate) return;

    if (selectingStartDate) {
      setInternalStartDate(newDate);
      setSelectingStartDate(false);

      if (internalEndDate && newDate > internalEndDate) {
        setInternalEndDate(null);
      }

      if (onChange) {
        const startDateString = `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}-${String(newDate.getDate()).padStart(2, '0')}`;
        onChange({ start: startDateString, end: null });
      }
    } else {
      if (internalStartDate && newDate >= internalStartDate) {
        setInternalEndDate(newDate);

        const startDateString = `${internalStartDate.getFullYear()}-${String(internalStartDate.getMonth() + 1).padStart(2, '0')}-${String(internalStartDate.getDate()).padStart(2, '0')}`;
        const endDateString = `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}-${String(newDate.getDate()).padStart(2, '0')}`;

        if (onChange) {
          onChange({ start: startDateString, end: endDateString });
        }

        handleCalendarClose();
      } else {
        setInternalStartDate(newDate);
        setInternalEndDate(null);
        setSelectingStartDate(false);
      }
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ width: width }}>
        <TextField
          size="small"
          value={formatDateRange()}
          onClick={handleCalendarOpen}
          placeholder={showPlaceholder ? placeholder : undefined}
          disabled={disabled}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <img
                src="assets/icons/icon-calendar.svg"
                alt="Calendar"
                style={{
                  cursor: 'pointer',
                  width: '20px',
                  height: '20px',
                }}
              />
            ),
            sx: {
              cursor: 'pointer',
              '& .MuiInputBase-input': {
                cursor: 'pointer',
              },
            },
          }}
          sx={{
            width: '100%',
            '& .MuiInputBase-root': {
              height: '32px',
              fontSize: '14px',
            },
          }}
        />

        <Popover
          open={isCalendarOpen}
          anchorEl={anchorEl}
          onClose={handleCalendarClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {selectingStartDate ? 'Select Start Date' : 'Select End Date'}
              </Typography>
            </Box>

            <StaticDatePicker
              value={null}
              onChange={handleDateSelect}
              minDate={minDate}
              slots={{
                toolbar: () => null,
              }}
              slotProps={{
                actionBar: {
                  actions: [],
                },
              }}
            />

            {internalStartDate && internalEndDate && (
              <Box sx={{ mt: 2, p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                  Selected Range:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {internalStartDate.toLocaleDateString()} - {internalEndDate.toLocaleDateString()}
                </Typography>
              </Box>
            )}
          </Paper>
        </Popover>
      </Box>
    </LocalizationProvider>
  );
};

export default DatePicker;
